import { useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import type { GenerationSalesMaster } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { entitySourceOf, useEditModalForm } from '~/hooks/useEditModalForm'
import { convertPlantMasterListItems } from '~/hooks/useListPlantMasters'
import { todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  convertBgMasterListItems,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { GenerationSalesPlanMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/components/GenerationSalesPlanMasterFormFields'
import { useGetGenerationSalesMaster } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/hooks/useGetGenerationSalesMaster'
import { useGetGenerationSalesPlanMasterFormOptions } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/hooks/useGetGenerationSalesPlanMasterFormOptions'
import { useUpdateGenerationSalesMaster } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/hooks/useUpdateGenerationSalesMaster'
import {
  sortBgRowsForEdit,
  sortPlantRowsForEdit,
} from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/rowOrder'
import {
  type GenerationSalesMasterFormValues,
  buildGenerationSalesMasterFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/schema'
import {
  plantCodeForNotFound,
  resolveGenerationSalesMasterDetailedError,
} from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/serverError'
import type { GenerationSalesMasterRow } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/types'
import { useGetSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useGetSubmitterMaster'
import {
  convertListSubmitterMastersResponse,
  useListSubmitterMasters,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'
import { deriveBgAddDisabledReason } from '~/pages/AdminOcctoMaster/tabs/_common/models/deriveBgAddDisabledReason'
import { findBgsOutOfPeriod } from '~/pages/AdminOcctoMaster/tabs/_common/models/planMasterBgValidation'

type Props = {
  isOpen: boolean
  row: GenerationSalesMasterRow
  onClose: () => void
}

const toFormValues = (
  fetched: GenerationSalesMaster,
  bgCodeById: ReadonlyMap<string, string>,
): GenerationSalesMasterFormValues => {
  return {
    submitterMasterId: fetched.submitterMasterId,
    submitterCode: fetched.submitterCode,
    note: fetched.note ?? '',
    // codes / bgs / plants は全件 round-trip (full replacement PUT セマンティクス)
    codes: fetched.codes.map((c) => ({
      code: c.code,
      codeName: c.codeName,
      isInternal: c.isInternal,
      validFrom:
        c.validFrom !== undefined
          ? {
              year: c.validFrom.year,
              month: c.validFrom.month,
              day: c.validFrom.day,
            }
          : todayCivilDate(),
      validTo:
        c.validTo !== undefined
          ? {
              year: c.validTo.year,
              month: c.validTo.month,
              day: c.validTo.day,
            }
          : undefined,
    })),
    bgs: sortBgRowsForEdit(fetched.bgs, bgCodeById).map((b) => ({
      bgMasterId: b.bgMasterId,
      validFrom:
        b.validFrom !== undefined
          ? {
              year: b.validFrom.year,
              month: b.validFrom.month,
              day: b.validFrom.day,
            }
          : todayCivilDate(),
      validTo:
        b.validTo !== undefined
          ? {
              year: b.validTo.year,
              month: b.validTo.month,
              day: b.validTo.day,
            }
          : undefined,
    })),
    plants: sortPlantRowsForEdit(fetched.plants).map((p) => ({
      plantMasterId: p.plantMasterId,
      bgMasterId: p.bgMasterId,
      validFrom:
        p.validFrom !== undefined
          ? {
              year: p.validFrom.year,
              month: p.validFrom.month,
              day: p.validFrom.day,
            }
          : todayCivilDate(),
      validTo:
        p.validTo !== undefined
          ? {
              year: p.validTo.year,
              month: p.validTo.month,
              day: p.validTo.day,
            }
          : undefined,
    })),
    // AC-05: 保存済みの ValidFrom (適用開始日、編集可能) / ValidTo を表示する。
    validFrom:
      fetched.validFrom !== undefined
        ? {
            year: fetched.validFrom.year,
            month: fetched.validFrom.month,
            day: fetched.validFrom.day,
          }
        : todayCivilDate(),
    validTo:
      fetched.validTo !== undefined
        ? {
            year: fetched.validTo.year,
            month: fetched.validTo.month,
            day: fetched.validTo.day,
          }
        : undefined,
  }
}

/**
 * 発電販売計画マスタ 編集モーダル。
 *
 * useGetGenerationSalesMaster で full entity (codes / bgs / plants 含む) を取得して
 * form を populate する。
 * submitterCode / submitterMasterId は登録後変更不可のため readonly で表示する。
 * note のみ編集可。codes / bgs / plants は read-only サマリ (件数) を表示し、
 * 将来の詳細編集 UI (別 PR) 実装までは更新時も既存値をそのまま渡す。
 */
export const EditGenerationSalesPlanMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateGenerationSalesMaster()
  const getQuery = useGetGenerationSalesMaster(row.id)
  const { data, isError, error } = getQuery
  // 集約 RPC: 発電区分でフィルタ済みの bgs を取得 (所属BG section 用)
  const { data: formOptions } = useGetGenerationSalesPlanMasterFormOptions()
  // codes section 用: 全件フェッチ (BG7 スコープ外、既存 hook 維持)
  const { data: allBgMastersData, isPending: isBgListPending } =
    useListBgMasters()
  const { data: submitterData } = useListSubmitterMasters()
  const { data: tradingPartnersData } = useListTradingPartners()

  // 集約 RPC の bgs (発電区分フィルタ済み): 所属BG section 用 (area narrow のみ client-side)
  const bgMasters = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.bgs)
        : [],
    [formOptions],
  )
  // 集約 RPC の plants (区分フィルタなし全件、AC-05): 所属発電所 section 用
  // (area narrow は client-side、AC-03)
  const plantMasters = useMemo(
    () =>
      formOptions !== undefined
        ? convertPlantMasterListItems(formOptions.plants)
        : [],
    [formOptions],
  )
  // codes section 用: 全件 BG / submitter (BG7 スコープ外、既存 fetch 維持)。
  // list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
  // PR #5920 レビュー指摘)。
  const allBgMasters = useMemo(
    () =>
      allBgMastersData !== undefined
        ? convertListBgMastersResponse(allBgMastersData)
        : undefined,
    [allBgMastersData],
  )
  const submitters = useMemo(
    () =>
      submitterData !== undefined
        ? convertListSubmitterMastersResponse(submitterData)
        : undefined,
    [submitterData],
  )
  const tradingPartners = useMemo(
    () => tradingPartnersData?.items ?? [],
    [tradingPartnersData],
  )

  const fetched = data?.generationSalesMaster

  // 保存の検査 (期間の重なり・所属 BG が BG 情報に無い) の文言に入れるコードの引き当て。
  // 系統コードは候補 + 保存済みの行 (候補から消えた系統コードも引けるように)。
  const plantCodeById = useMemo(() => {
    const map = new Map(plantMasters.map((p) => [p.id, p.plantCode]))
    for (const p of fetched?.plants ?? []) map.set(p.plantMasterId, p.plantCode)
    return map
  }, [plantMasters, fetched?.plants])
  const formSchema = useMemo(
    () =>
      buildGenerationSalesMasterFormSchema({
        bgCodeById: new Map(
          (allBgMasters ?? []).map((bg) => [bg.id, bg.bgCode]),
        ),
        plantCodeById,
      }),
    [allBgMasters, plantCodeById],
  )

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave: hookHandleSave,
    setError,
    getValues,
    isDirty,
    loadStatus,
  } = useEditModalForm<GenerationSalesMaster, GenerationSalesMasterFormValues>({
    // BG 情報の並び (BG コード順) に BG マスタの一覧が要るので、一覧が届くまでは埋めない。
    source: entitySourceOf(getQuery, (d) =>
      isBgListPending ? undefined : d.generationSalesMaster,
    ),
    toFormValues: (entity) =>
      toFormValues(
        entity,
        new Map((allBgMasters ?? []).map((bg) => [bg.id, bg.bgCode])),
      ),
    schema: formSchema,
    defaultValues: {
      submitterMasterId: '',
      submitterCode: '',
      note: '',
      codes: [],
      bgs: [],
      plants: [],
      validFrom: todayCivilDate(),
      validTo: undefined,
    },
    onSubmit: async (values, { expectedUpdatedAt }) => {
      await mutateAsync({
        id: row.id,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // codes / bgs / plants は全件 round-trip (full replacement PUT セマンティクス)
        codes: values.codes.map((c) => ({
          code: c.code,
          codeName: c.codeName,
          isInternal: c.isInternal,
          validFrom: c.validFrom,
          validTo: c.validTo,
        })),
        bgs: values.bgs.map((b) => ({
          bgMasterId: b.bgMasterId,
          validFrom: b.validFrom,
          validTo: b.validTo,
        })),
        plants: values.plants.map((p) => ({
          plantMasterId: p.plantMasterId,
          bgMasterId: p.bgMasterId,
          validFrom: p.validFrom,
          validTo: p.validTo,
        })),
        validFrom: values.validFrom,
        validTo: values.validTo,
        expectedUpdatedAt,
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.generationSalesPlanMaster.serverError.aborted',
      [Code.NotFound]:
        'page:adminOcctoMaster.generationSalesPlanMaster.serverError.notFound',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.generationSalesPlanMaster.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.generationSalesPlanMaster.serverError.unknown',
    // MEMBER_BG_NOT_FOUND / PLANT_NOT_FOUND は Update でも到達しうる
    // (bgs[] / plants[] は編集可能なので Create/Edit 両方に配線する)。
    // duplicate reason は Create のみ発生
    // (plan_category は Update で変更不可) のため values をそのまま渡す。
    // PLANT_NOT_FOUND の文言には、サーバーが返す見つからない発電所の id から引いた系統コードを入れる。
    resolveDetailedError: (err, tFn, values) =>
      resolveGenerationSalesMasterDetailedError(err, tFn, {
        ...values,
        plantCode: plantCodeForNotFound(err, plantCodeById),
      }),
  })

  // 他社コード候補の area 絞り込み用: submitterCode と一致する submitter の areaCode を解決
  const submitterAreaCode = useMemo(() => {
    const code = fetched?.submitterCode
    if (code === undefined || code === '') return undefined
    return (submitters ?? []).find((s) => s.submitterCode === code)?.areaCode
  }, [fetched?.submitterCode, submitters])

  // AC-06: 編集対象の計画提出者の所属BG (members) を個別取得する
  // (CreateGenerationSalesPlanMasterModal と同じ理由、集約 RPC の
  // SubmitterMasterListItem は members field を持たない)。
  const { data: selectedSubmitterDetail, isError: isSubmitterDetailError } =
    useGetSubmitterMaster(fetched?.submitterMasterId)
  const submitterMembers = selectedSubmitterDetail?.submitterMaster?.members
  // 所属 BG の取得中は候補が絞れない (全発電 BG が出る) ので、候補の操作と保存を止める。
  // 止めないと所属していない BG を選んで保存できてしまう。
  const isSubmitterDetailPending =
    fetched !== undefined &&
    submitterMembers === undefined &&
    !isSubmitterDetailError
  const bgAddDisabledReason = useMemo(
    () => deriveBgAddDisabledReason(fetched?.submitterCode, submitterMembers),
    [fetched?.submitterCode, submitterMembers],
  )
  // BG 情報の候補 = 計画提出者の所属 BG のうち発電 BG (集約 RPC は発電区分で絞り込み済み)。
  // 所属 BG が取得できるまでは絞らない (取得中に候補が空と誤表示しない)。
  const bgCandidates = useMemo(
    () =>
      submitterMembers === undefined
        ? bgMasters
        : bgMasters.filter((bg) =>
            submitterMembers.some((m) => m.bgMasterId === bg.id),
          ),
    [bgMasters, submitterMembers],
  )

  // AC-01: codes[] 配列全体の min(1) 違反 (field-level 表示)。
  const codesFieldError = errors.codes as
    | { root?: { message?: string }; message?: string }
    | undefined
  const codesErrorMessage =
    codesFieldError?.root?.message ??
    (typeof codesFieldError?.message === 'string'
      ? codesFieldError.message
      : undefined)

  // 選択済み所属 BG が validFrom narrow 後の候補外 (= 有効期間外) なら
  // submit を block する。fallback option で UI 上は残るが、そのまま save
  // すると失効 BG を含むレコードが RPC 送信される silent invalid submission
  // を防ぐ (物理削除は AC-06 尊重で除外、helper 参照)。
  const validateBgsPeriodBeforeSubmit = (): boolean => {
    const values = getValues()
    const recordValidFrom = values.validFrom
    const bgCandidates = bgMasters.filter(
      (bg) =>
        (submitterAreaCode === undefined ||
          bg.areaCode === submitterAreaCode) &&
        (recordValidFrom === undefined ||
          isActiveOn(bg.validFrom, bg.validTo, recordValidFrom)),
    )
    const outOfPeriod = findBgsOutOfPeriod(
      values.bgs ?? [],
      bgCandidates,
      // allBgMastersData 未取得時は undefined を渡し、helper 側で
      // fail-closed (候補外を全て有効期間外扱い) にする。
      allBgMastersData !== undefined ? allBgMasters : undefined,
    )
    if (outOfPeriod.length === 0) return true
    for (const { index } of outOfPeriod) {
      setError(`bgs.${index}.bgMasterId`, {
        message: t(
          'page:adminOcctoMaster.generationSalesPlanMaster.form.bgMasterOutOfPeriod',
        ),
      })
    }
    return false
  }

  // 期間外 BG の pre-check を hook の handleSave の前に挟む。
  const handleSave = (): void => {
    if (!validateBgsPeriodBeforeSubmit()) return
    hookHandleSave()
  }

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      isSaveDisabled={isSubmitterDetailPending}
      loadError={
        isError ? (
          <Alert
            status="error"
            title={mapMasterGetError(
              t,
              error,
              'page:adminOcctoMaster.generationSalesPlanMaster.serverError',
            )}
          />
        ) : undefined
      }
      loadStatus={loadStatus}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #pm-edit-modal) は `.ecl-modal--lg`。
      size="lg"
      title={t(
        'page:adminOcctoMaster.generationSalesPlanMaster.modal.editTitle',
      )}
    >
      <RegionLoading isLoading={isSubmitterDetailPending}>
        <GenerationSalesPlanMasterFormFields
          allBgMasters={allBgMasters}
          bgAddDisabledReason={bgAddDisabledReason}
          bgMasters={bgCandidates}
          codesErrorMessage={codesErrorMessage}
          control={control}
          errors={errors}
          isEdit
          planCategory={fetched?.planCategory}
          plantMasters={plantMasters}
          selfSubmitterCode={fetched?.submitterCode}
          submitterAreaCode={submitterAreaCode}
          submitters={submitters}
          tradingPartners={tradingPartners}
        />
      </RegionLoading>
    </CrudModalLayout>
  )
}
